import React, { useState, useEffect } from 'react';
import { DialogContent, useFlashMessage } from '@indigo-ag/component-module';
import { AgUpdateSacksHectaresInput, FinancingSacksHectares } from '@indigo-ag/schema';
import { ApolloError, useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { EnhancedDialog, InputNumberMask, ModalActionButtons, utils } from 'shared';
import { updateSacksHectares } from 'features/pricing/queries';
import { SingleRow } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/SingleRow';

export interface UpdatePricingModalProps {
  sacksHectares: FinancingSacksHectares;
  closeModal: () => void;
  open: boolean;
}

export const UpdatePricingModal: React.FunctionComponent<UpdatePricingModalProps> = ({
  closeModal,
  open,
  sacksHectares
}) => {
  const getInitialHectaresInfo = () => ({
    id: sacksHectares.id,
    pricePerBag: sacksHectares.pricePerBag,
    bagsPerHectares: sacksHectares.bagsPerHectares
  });
  /**
   * State
   */
  const [sacksHectaresNewInfo, setSacksHectaresNewInfo] = useState<AgUpdateSacksHectaresInput>(
    getInitialHectaresInfo()
  );

  /**
   * Mutations
   */
  const [updateSacksHectaresFn] = useMutation(updateSacksHectares);

  useEffect(() => {
    if (open) {
      setSacksHectaresNewInfo(getInitialHectaresInfo());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSacksHectaresValues = (
    key: keyof Pick<AgUpdateSacksHectaresInput, 'pricePerBag' | 'bagsPerHectares'>
  ) => {
    return (n: number | null) => {
      setSacksHectaresNewInfo({ ...sacksHectaresNewInfo, [key]: n ? Number(n) : undefined });
    };
  };

  const { addFlashMessage } = useFlashMessage();

  const updateSacksHectaresInfo = () => {
    return updateSacksHectaresFn({
      variables: {
        sacksHectaresUpdatedInfo: sacksHectaresNewInfo
      }
    })
      .then(() => {
        addFlashMessage({
          content: utils.text.firstLetterUppercase(
            `${i18next.t('pricingInformation')} ${i18next.t('hasBeenUpdatedSuccessfully')}`
          ),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });

        // Error just for internal use, to prevent closing modal on error
        throw new Error('error saving pricing information');
      });
  };

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={utils.text.toTitleCase(i18next.t('pricingInformation'))}>
      <DialogContent>
        <SingleRow
          title={i18next.t('state')}
          content={`${sacksHectares.state.name} (${sacksHectares.state.code})`}
        ></SingleRow>
        <SingleRow title={i18next.t('campaign')} content={sacksHectares.campaign.name}></SingleRow>
        <InputNumberMask
          label={i18next.t('bagsPriceBase')}
          onNumberChange={updateSacksHectaresValues('pricePerBag')}
          numberValue={sacksHectaresNewInfo.pricePerBag}
          nullable
        />
        <InputNumberMask
          label={i18next.t('bagsBase')}
          onNumberChange={updateSacksHectaresValues('bagsPerHectares')}
          numberValue={sacksHectaresNewInfo.bagsPerHectares}
          nullable
        />
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        save={updateSacksHectaresInfo}
      />
    </EnhancedDialog>
  );
};
